import { useEffect, useState, useRef } from "react";
import Header from "@/components/Header";
import HeroSection from "@/components/HeroSection";
import AboutSection from "@/components/AboutSection";
import ServicesSection from "@/components/ServicesSection";
import CallToAction from "@/components/CallToAction";
import AreasSection from "@/components/AreasSection";
import GallerySection from "@/components/GallerySection";
import TestimonialsSection from "@/components/TestimonialsSection";
import ContactSection from "@/components/ContactSection";
import ProjectTimelineSection from "@/components/ProjectTimelineSection";
import EstimationCalculator from "@/components/EstimationCalculator";
import BookingSection from "@/components/BookingSection";
import Footer from "@/components/Footer";
import ScrollToTop from "@/components/ScrollToTop";
import Lightbox from "@/components/Lightbox";
import { Toaster } from "@/components/ui/toaster";
import { Router, Route, Switch, useLocation } from 'wouter';
import { AuthProvider, useAuth } from "@/hooks/use-auth";
import { ProtectedRoute } from "@/lib/protected-route";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";

// Import pages
import Gallery from "@/pages/Gallery";
import Admin from "@/pages/Admin";
import BookingManagement from "@/pages/BookingManagement";
import AuthPage from "@/pages/auth-page";
import VerifyEmail from "@/pages/VerifyEmail";
import NotFound from "@/pages/not-found";

// Main content component
function MainContent() {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImage, setCurrentImage] = useState<{
    src: string;
    alt: string;
    category: string;
    title?: string;
    description?: string;
    location?: string;
  } | null>(null);
  const [location] = useLocation();
  const { user } = useAuth();

  const aboutRef = useRef<HTMLDivElement>(null);
  const servicesRef = useRef<HTMLDivElement>(null);
  const galleryRef = useRef<HTMLDivElement>(null);
  const testimonialsRef = useRef<HTMLDivElement>(null);
  const contactRef = useRef<HTMLDivElement>(null);
  const homeRef = useRef<HTMLDivElement>(null);
  const areasRef = useRef<HTMLDivElement>(null);
  const timelineRef = useRef<HTMLDivElement>(null);
  const estimationRef = useRef<HTMLDivElement>(null);
  const bookingRef = useRef<HTMLDivElement>(null);

  const scrollToSection = (ref: React.RefObject<HTMLDivElement>) => {
    if (ref.current) {
      window.scrollTo({
        top: ref.current.offsetTop - 80, // 80px header offset
        behavior: "smooth"
      });
    }
  };

  const openLightbox = (index: number, galleryImage: {
    src: string;
    alt: string;
    category: string;
    title?: string;
    description?: string;
    location?: string;
  }) => {
    setCurrentImage(galleryImage);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setCurrentImage(null);
  };

  useEffect(() => {
    // Handle keyboard events for lightbox navigation
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!lightboxOpen) return;

      if (e.key === "Escape") closeLightbox();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [lightboxOpen]);

  return (
    <div className="relative">
      {/* Main content starts here */}
      
      {/* Main Header - Only shown on home page */}
      {location === '/' && (
        <Header 
          onHomeClick={() => scrollToSection(homeRef)}
          onAboutClick={() => scrollToSection(aboutRef)}
          onServicesClick={() => scrollToSection(servicesRef)}
          onGalleryClick={() => scrollToSection(galleryRef)}
          onTestimonialsClick={() => scrollToSection(testimonialsRef)}
          onTimelineClick={() => scrollToSection(timelineRef)}
          onEstimationClick={() => scrollToSection(estimationRef)}
          onBookingClick={() => scrollToSection(bookingRef)}
          onContactClick={() => scrollToSection(contactRef)}
        />
      )}

      <Switch>
        <Route path="/">
          <>
            <div ref={homeRef}>
              <HeroSection 
                onExploreClick={() => scrollToSection(servicesRef)}
                onContactClick={() => scrollToSection(contactRef)}
              />
            </div>

            <div ref={aboutRef}>
              <AboutSection onContactClick={() => scrollToSection(contactRef)} />
            </div>

            <div ref={servicesRef}>
              <ServicesSection onContactClick={() => scrollToSection(contactRef)} />
            </div>

            <CallToAction onContactClick={() => scrollToSection(contactRef)} />

            <div ref={areasRef}>
              <AreasSection onContactClick={() => scrollToSection(contactRef)} />
            </div>

            <div ref={galleryRef}>
              <GallerySection 
                onImageClick={openLightbox}
                onContactClick={() => scrollToSection(contactRef)}
              />
            </div>

            <div ref={testimonialsRef}>
              <TestimonialsSection onContactClick={() => scrollToSection(contactRef)} />
            </div>

            <div ref={timelineRef}>
              <ProjectTimelineSection onContactClick={() => scrollToSection(contactRef)} />
            </div>

            <div ref={estimationRef}>
              <EstimationCalculator onContactClick={() => scrollToSection(contactRef)} />
            </div>

            <div ref={bookingRef}>
              <BookingSection onContactClick={() => scrollToSection(contactRef)} />
            </div>

            <div ref={contactRef}>
              <ContactSection />
            </div>
            <Footer 
              onHomeClick={() => scrollToSection(homeRef)}
              onAboutClick={() => scrollToSection(aboutRef)}
              onServicesClick={() => scrollToSection(servicesRef)}
              onGalleryClick={() => scrollToSection(galleryRef)}
              onTestimonialsClick={() => scrollToSection(testimonialsRef)}
              onAreasClick={() => scrollToSection(areasRef)}
              onTimelineClick={() => scrollToSection(timelineRef)}
              onEstimationClick={() => scrollToSection(estimationRef)}
              onBookingClick={() => scrollToSection(bookingRef)}
              onContactClick={() => scrollToSection(contactRef)}
            />
          </>
        </Route>
        <Route path="/gallery"><Gallery /></Route>
        <Route path="/auth"><AuthPage /></Route>
        <Route path="/verify-email"><VerifyEmail /></Route>
        <ProtectedRoute path="/admin" component={Admin} adminOnly={true} />
        <ProtectedRoute path="/bookings" component={BookingManagement} adminOnly={true} />
        <Route><NotFound /></Route>
      </Switch>

      <ScrollToTop />

      {lightboxOpen && currentImage && (
        <Lightbox
          isOpen={lightboxOpen}
          image={currentImage}
          onClose={closeLightbox}
        />
      )}

      <Toaster />
    </div>
  );
}

// Main App component
function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Router>
          <MainContent />
        </Router>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;